"use client"

import * as React from "react"
import { CameraIcon, TrashSimpleIcon, UploadSimpleIcon } from "@phosphor-icons/react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "cn"

import { Avatar, AvatarFallback, AvatarImage } from "./avatar"
import { Button } from "./button"
import { Spinner } from "./spinner"

const avatarUploadFallbackVariants = cva("typo-h4", {
  variants: {
    tone: {
      primary: "bg-primary-100 text-primary-800 dark:bg-primary-950 dark:text-primary-200",
      secondary: "bg-greyscale-600 text-greyscale-50 dark:bg-greyscale-500",
    },
  },
  defaultVariants: { tone: "primary" },
})

function AvatarUpload({
  src,
  alt = "",
  fallback,
  tone,
  accept = "image/jpeg,image/png,image/webp",
  uploadLabel = "Upload photo",
  removeLabel = "Remove photo",
  removingLabel,
  hint,
  error,
  pending = false,
  removing = false,
  disabled = false,
  onSelect,
  onRemove,
  className,
}: VariantProps<typeof avatarUploadFallbackVariants> & {
  src?: string | null
  alt?: string
  /** Initials or an icon, shown when there is no picture. */
  fallback: React.ReactNode
  accept?: string
  uploadLabel?: string
  removeLabel?: string
  removingLabel: string
  hint?: React.ReactNode
  error?: React.ReactNode
  /** A picture is chosen but not saved yet. */
  pending?: boolean
  removing?: boolean
  disabled?: boolean
  onSelect: (file: File) => void
  /** Leave out when there is no saved picture to remove. */
  onRemove?: () => void
  className?: string
}) {
  const input = React.useRef<HTMLInputElement>(null)
  const pick = () => input.current?.click()

  return (
    <div data-slot="avatar-upload" className={cn("flex flex-wrap items-center gap-x-5 gap-y-3", className)}>
      <button
        type="button"
        onClick={pick}
        disabled={disabled}
        aria-label={uploadLabel}
        className="group/avatar-upload relative shrink-0 rounded-full outline-none focus-visible:ring-3 focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <Avatar
          data-pending={pending || undefined}
          className="size-20 transition-shadow data-pending:ring-2 data-pending:ring-primary data-pending:ring-offset-2 data-pending:ring-offset-card"
        >
          {src && <AvatarImage src={src} alt={alt} />}
          <AvatarFallback className={avatarUploadFallbackVariants({ tone })}>{fallback}</AvatarFallback>
        </Avatar>
        <span
          aria-hidden="true"
          className="absolute inset-0 flex items-center justify-center rounded-full bg-black/45 text-white opacity-0 transition-opacity group-hover/avatar-upload:opacity-100 group-focus-visible/avatar-upload:opacity-100 group-disabled/avatar-upload:opacity-0"
        >
          <CameraIcon className="size-6" />
        </span>
      </button>
      <input
        ref={input}
        type="file"
        accept={accept}
        hidden
        tabIndex={-1}
        onChange={(event) => {
          const file = event.target.files?.[0]
          event.target.value = ""
          if (file) onSelect(file)
        }}
      />
      <div className="flex min-w-0 flex-1 basis-56 flex-col gap-2">
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" size="sm" shape="pill" disabled={disabled} onClick={pick}>
            <UploadSimpleIcon data-icon="inline-start" />
            {uploadLabel}
          </Button>
          {onRemove && (
            <Button type="button" variant="ghost" size="sm" shape="pill" disabled={disabled || removing} onClick={onRemove}>
              {removing ? <Spinner data-icon="inline-start" aria-label={removingLabel} /> : <TrashSimpleIcon data-icon="inline-start" />}
              {removeLabel}
            </Button>
          )}
        </div>
        {hint && <div className="typo-caption text-muted-foreground">{hint}</div>}
        {error && (
          <p role="alert" className="typo-caption text-destructive">
            {error}
          </p>
        )}
      </div>
    </div>
  )
}

export { AvatarUpload }
